import { CancelTokenSource } from "axios";
import { GetterTree } from "vuex";
import { ClientProject } from "@/models/ClientProject";
import { Lead, ClientEditViewModel, LeadEditViewModel, LeadsPageViewModel, RefreshGridMode, PhonePopupData } from "@/models/leads/Lead";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { AdTitlesByLeads, Assignee, ChannelMultiselectItem, MultiselectItem, SelectListItem } from "@/models/MultiselectItem";
import { TableSettings } from "@/models/TableSettings";
import { LeadStatistic, LeadStatisticExtended, PhoneLeadStats } from "@/models/leads/LeadStatistic";
import { LeadStatus } from "@/models/leads/LeadStatus";
import { LeadsState } from "@/models/states/LeadsState";
import { RootState } from "@/models/states/RootState";
import { LeadsPayload } from "@/services/api/ApiService";

const getters: GetterTree<LeadsState, RootState> = {
  adGroups(state): MultiselectItem[] {
    return state.adGroups;
  },

  adTitles(state): AdTitlesByLeads[] {
    return state.adTitles;
  },

  additionalStatuses(state): SelectListItem[] {
    return state.additionalStatuses;
  },

  assets(state): ClientProject[] {
    return state.assets;
  },

  assignees(state): Assignee[] {
    return state.assignees;
  },

  channels(state): ChannelMultiselectItem[] {
    return state.channels;
  },

  companies(state): MultiselectItem[] {
    return state.companies;
  },

  columns(state): GridColumn[] {
    return state.columns;
  },

  columnsFilters(state): FilterCollection | undefined {
    return state.columnsFilters;
  },

  commitments(state): MultiselectItem[] {
    return state.commitments;
  },

  processes(state): MultiselectItem[] {
    return state.processes;
  },

  datePeriod(state): Date[] {
    return state.datePeriod;
  },

  deactivateStats(state): boolean {
    return state.deactivateStats;
  },

  graphsHiddenOnScroll(state): boolean {
    return state.graphsHiddenOnScroll;
  },

  clientEditViewModel(state): ClientEditViewModel | undefined {
    return state.clientEditViewModel;
  },

  clientsDatePeriod(state): Date[] {
    return state.clientsDatePeriod;
  },

  clientsSearchValue(state): string {
    return state.clientsSearchValue;
  },

  clientsSelectedAdditionalStatusesIds(state): number[] {
    return state.clientsSelectedAdditionalStatusesIds;
  },

  clientsSelectedAdGroupsIds(state): string[] {
    return state.clientsSelectedAdGroupsIds;
  },

  clientsSelectedAdTitles(state): string[] {
    return state.clientsSelectedAdTitles;
  },

  clientsSelectedAssetsIds(state): string[] {
    return state.clientsSelectedAssetsIds;
  },

  clientsSelectedAssigneesIds(state): number[] {
    return state.clientsSelectedAssigneesIds;
  },

  clientsSelectedCallStatusesIds(state): number[] {
    return state.clientsSelectedCallStatusesIds;
  },

  clientsSelectedChannelMetadataTypesIds(state): number[] {
    return state.clientsSelectedChannelMetadataTypesIds;
  },

  clientsSelectedChannelsIds(state): number[] {
    return state.clientsSelectedChannelsIds;
  },

  clientsSelectedCompaniesIds(state): number[] {
    return state.clientsSelectedCompaniesIds;
  },

  clientsSelectedCommitmentsIds(state): number[] {
    return state.clientsSelectedCommitmentsIds;
  },

  clientsSelectedProcessesIds(state): number[] {
    return state.clientsSelectedProcessesIds;
  },

  clientsSelectedFoundMetadataTypesIds(state): number[] {
    return state.clientsSelectedFoundMetadataTypesIds;
  },

  clientsSelectedLeadStatusesIds(state): LeadStatus[] {
    return state.clientsSelectedLeadStatusesIds;
  },

  clientsSelectedSaleStatusesIds(state): LeadStatus[] {
    return state.clientsSelectedSaleStatusesIds;
  },

  clientsSelectedTags(state): string[] {
    return state.clientsSelectedTags;
  },

  clientsSelectedPriceType(state): string {
    return state.clientsSelectedPriceType;
  },

  leadEditViewModel(state): LeadEditViewModel | undefined {
    return state.leadEditViewModel;
  },

  leads(state): DataSourceResult {
    return state.leads;
  },

  leadsCancelTokenSource(state): CancelTokenSource | undefined {
    return state.leadsCancelTokenSource;
  },

  leadsLoading(state): boolean {
    return state.leadsLoading;
  },

  leadsPageData(state): LeadsPageViewModel | undefined {
    return state.leadsPageData;
  },

  leadsPayload(state): LeadsPayload | undefined {
    return state.leadsPayload;
  },

  leadsStatisticByAdditionalStatus(state): LeadStatistic[] {
    return state.leadsStatisticByAdditionalStatus;
  },

  leadsStatisticByCallStatus(state): LeadStatistic[] {
    return state.leadsStatisticByCallStatus;
  },

  leadsStatisticByCampaigns(state): LeadStatistic[] {
    return state.leadsStatisticByCampaigns;
  },

  leadsStatisticByPhoneAndForms(state): LeadStatistic[] {
    return state.leadsStatisticByPhoneAndForms;
  },

  leadsStatisticBySaleStatus(state): LeadStatistic[] {
    return state.leadsStatisticBySaleStatus;
  },

  leadsStatisticChannelMetadataType(state): LeadStatistic[] {
    return state.leadsStatisticChannelMetadataType;
  },

  leadsStatisticDealMeetingsLeads(state): LeadStatisticExtended[] {
    return state.leadsStatisticDealMeetingsLeads;
  },

  leadsStatisticPhones(state): PhoneLeadStats | undefined {
    return state.leadsStatisticPhones;
  },

  needToRefreshLeadsGrid(state): RefreshGridMode {
    return state.needToRefreshLeadsGrid;
  },

  page(state): number {
    return state.page;
  },

  searchValue(state): string {
    return state.searchValue;
  },

  selectedAdditionalStatusesIds(state): number[] {
    return state.selectedAdditionalStatusesIds;
  },

  selectedAdGroupsIds(state): string[] {
    return state.selectedAdGroupsIds;
  },

  selectedAdTitles(state): string[] {
    return state.selectedAdTitles;
  },

  selectedAssetsIds(state): string[] {
    return state.selectedAssetsIds;
  },

  selectedAssigneesIds(state): number[] {
    return state.selectedAssigneesIds;
  },

  selectedCallStatusesIds(state): number[] {
    return state.selectedCallStatusesIds;
  },

  selectedChannelMetadataTypesIds(state): number[] {
    return state.selectedChannelMetadataTypesIds;
  },

  selectedChannelsIds(state): number[] {
    return state.selectedChannelsIds;
  },

  selectedCompaniesIds(state): number[] {
    return state.selectedCompaniesIds;
  },

  selectedCommitmentsIds(state): number[] {
    return state.selectedCommitmentsIds;
  },

  selectedProcessesIds(state): number[] {
    return state.selectedProcessesIds;
  },

  selectedFoundMetadataTypesIds(state): number[] {
    return state.selectedFoundMetadataTypesIds;
  },

  selectedLead(state): Lead | undefined {
    return state.selectedLead;
  },

  selectedClient(state): Lead | undefined {
    return state.selectedClient;
  },

  selectedClientId(state): number {
    return state.selectedClientId;
  },

  selectedLeadGridColumnsIds(state): number[] {
    return state.selectedLeadGridColumnsIds;
  },

  selectedLeadStatusesIds(state): LeadStatus[] {
    return state.selectedLeadStatusesIds;
  },

  selectedPriceType(state): string {
    return state.selectedPriceType;
  },

  selectedSaleStatusesIds(state): LeadStatus[] {
    return state.selectedSaleStatusesIds;
  },

  selectedTags(state): string[] {
    return state.selectedTags;
  },

  showDeleted(state): boolean {
    return state.showDeleted;
  },

  showDeletedClientLeads(state): boolean {
    return state.showDeletedClientLeads;
  },

  showFavourite(state): boolean {
    return state.showFavourite;
  },

  showFavouriteClientLeads(state): boolean {
    return state.showFavouriteClientLeads;
  },

  showGraphs(state): boolean {
    return state.showGraphs;
  },

  showGraphsClientLeads(state): boolean {
    return state.showGraphsClientLeads;
  },

  showNotActiveCampaign(state): boolean {
    return state.showNotActiveCampaign;
  },

  showNotActiveCampaignClientLeads(state): boolean {
    return state.showNotActiveCampaignClientLeads;
  },

  sortDir(state): string {
    return state.sortDir;
  },

  sortField(state): string {
    return state.sortField;
  },

  tableSettings(state): TableSettings | undefined {
    return state.tableSettings;
  },

  tags(state): MultiselectItem[] {
    return state.tags;
  },

  uniqueByCampaign(state): boolean {
    return state.uniqueByCampaign;
  },

  uniqueByCampaignClientLeads(state): boolean {
    return state.uniqueByCampaignClientLeads;
  },

    phonePopupData(state): PhonePopupData {
        return state.phonePopupData;
    },
    hidePhonePopup(state): boolean {
        return !state.phonePopupData.showPopup;
    },

};
export default getters;
